import React, {useState, useRef, useCallback, useEffect} from "react";
import {useDispatch} from "react-redux";
import {useNavigate} from "react-router-dom";
import axios from "axios";
import prettyBytes from "pretty-bytes";

import consts from "../../libs/consts";
import LoadingSpinner from "../icon/LoadingSpinner";

/**
 * 자재파인더 모달
 * @param onSuccess
 * @param onFail
 * @param onNetworkError
 * @returns {Element}
 * @constructor
 */
const MeterialFinderModal = ({ isMobile, onSuccess, onFail, onNetworkError }) => {
    const navigate = useNavigate();

    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [isError, setIsError] = useState(false);
    const [isNetworkError, setIsNetworkError] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [imageUrl, setImageUrl] = useState('');
    const [filename, setFilename] = useState('');
    const [transferred, setTransferred] = useState(0);
    const [totalSize, setTotalSize] = useState(0);

    const dropzoneRef = useRef();

    const dispatch = useDispatch();

    /**
     * 파일 업로드
     * @param file
     * @returns {Promise<void>}
     */
    const doUpload = async (file) => {
        // 500MB를 초과한 경우, 에러 표시
        if(file.size > 500 * 1024 * 1024) {
            setIsError(true);
            return;
        }

        // jpg, png 파일이 아닌 경우, 에러 표시
        if (['image/jpeg', 'image/png'].includes(file.type) === false) {
            document.querySelector('#materialFinderUploader').value = ''; // <input type="file" /> 초기화
            setIsError(true);
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        const instance = axios.create({
            baseURL: consts.apiBasicUrl,
            timeout: 30000,
            headers: {'Content-Type': 'multipart/form-data'}
        });

        const res = await instance.postForm('/material-finder/upload', formData, {
            onUploadProgress: (progressEvent) => {
                const { loaded, total } = progressEvent;
                setTransferred(loaded);
                setTotalSize(total);
            }
        }).then((response) => {
            if(response.status !== 200) {
                setIsUploading(false);
                setIsSuccess(false);
                setIsError(true);
                setIsNetworkError(false);
                return;
            }

            setIsUploading(false);
            setIsSuccess(true);
            setIsError(false);
            setIsNetworkError(false);
            setFilename(file.name);
            setImageUrl(response.data.url);

            dispatch({type: 'closeMaterialFinder'})

            navigate('/search', { state: {
                isUploaded: true,
                uploadedImageUrl: response.data.url
            }});
        }).catch((_error) => {
            setIsUploading(false);
            setIsSuccess(false);
            setIsError(false);
            setIsNetworkError(true);
        })

        if(res) {
            onSuccess(res.data);
        }
    }

    /**
     * 파일 선택기로 이미지 파일을 선택한 경우
     * @param e
     * @returns {Promise<void>}
     */
    const handleMaterialFinderUpload = async (e) => {
        const file = e.target.files[0];

        setIsUploading(true);
        setFilename(file.name);
        setTotalSize(file.size);
        setTransferred(0);

        await doUpload(file);
    }

    const handleDragIn = useCallback(e => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDragOut = useCallback(e => {
        e.preventDefault();
        e.stopPropagation();

        setIsDragging(false);
    }, []);

    const handleDragOver = useCallback(e => {
        e.preventDefault();
        e.stopPropagation();

        setIsDragging(true);
    }, []);

    /**
     * 파일을 드랍한 경우
     * @type {(function(*): Promise<void>)|*}
     */
    const handleDrop = useCallback(async (e) => {
        e.preventDefault();
        e.stopPropagation();

        setIsDragging(false);

        if (e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];

            await doUpload(file);
        }
    }, [doUpload]);

    const initDragEvents = useCallback(() => {
        // 4개의 이벤트에 Listener를 등록합니다. (마운트 될때)
        if (dropzoneRef.current !== null && dropzoneRef.current !== undefined) {
            dropzoneRef.current.addEventListener("dragenter", handleDragIn);
            dropzoneRef.current.addEventListener("dragleave", handleDragOut);
            dropzoneRef.current.addEventListener("dragover", handleDragOver);
            dropzoneRef.current.addEventListener("drop", handleDrop);
        }
    }, [handleDragIn, handleDragOut, handleDragOver, handleDrop]);

    const resetDragEvents = useCallback(() => {
        // 앞서 말했던 4개의 이벤트에 Listener를 삭제합니다. (언마운트 될때)

        if (dropzoneRef.current !== null && dropzoneRef.current !== undefined) {
            dropzoneRef.current.removeEventListener("dragenter", handleDragIn);
            dropzoneRef.current.removeEventListener("dragleave", handleDragOut);
            dropzoneRef.current.removeEventListener("dragover", handleDragOver);
            dropzoneRef.current.removeEventListener("drop", handleDrop);
        }
    }, [handleDragIn, handleDragOut, handleDragOver, handleDrop]);

    useEffect(() => {
        initDragEvents();

        return () => resetDragEvents();
    }, [initDragEvents, resetDragEvents]);

    return (
        <div className={`modal-meterial-finder${isUploading ? ' is-uploading' : ''}`}>
            {!isSearching && <div className="modal-content">
                <header>
                    <h2>자재파인더<small>다양한 자재를 AI로 빠르게 검색해보세요.</small></h2>
                    <button className="close-btn" onClick={() => dispatch({type: 'closeMaterialFinder'})}>
                        <span>닫기</span></button>
                </header>
                <div className="modal-body">
                    <div className={`drop-zone${isError ? ' error' : '' }${isDragging ? ' dragging' : ''}`} ref={dropzoneRef} >
                        {isUploading && (
                            <div className="uploading-text"><span style={{ color: 'rgba(32, 166, 76, 1)' }}>이미지를 업로드 하고 있어요</span><br />이미지 업로드 도중 화면을 닫으시면 업로드가 중단될 수 있습니다.</div>
                        )}
                        {!isUploading && isError && (
                            <div className="uploader-error">
                                <b>이미지를 다시 업로드 해주세요.</b><br />
                                * 500MB 이하의 사진 한 장만 업로드 할 수 있습니다. (jpg,png 확장자)
                            </div>
                        )}
                        {!isUploading && (
                            <>
                                <label htmlFor="materialFinderUploader" style={{ position: "absolute", left: 0, top: 0, width: '100%', height: '100%' }}></label>
                                <input id="materialFinderUploader" type="file" accept="image/jpeg, image/png"
                                       onChange={handleMaterialFinderUpload}/>
                                {!isMobile && <>여기로 이미지를 드래그 해주세요<br/></>}
                                <span>
                                    {!isMobile &&<>또는</>}<button onClick={() => document.querySelector('#materialFinderUploader').click()}>여기를 클릭하여 이미지를 선택해주세요</button>
                                </span>
                                <em>* 500MB 이하의 사진 한 장만 업로드 할 수 있습니다. (jpg,png 확장자)</em>
                            </>
                        )}
                    </div>
                    {isUploading && (
                        <div className="uploader-status">
                            <div className="filename">{filename}</div>
                            <div className="progress">
                                <div className="progress-bar" style={{ width: transferred/totalSize*100 + '%' }}></div>
                            </div>
                            <div className="uploader-progress">
                                {prettyBytes(transferred, { space: false }).toUpperCase()} of {prettyBytes(totalSize, { space: false }).toUpperCase()} <div className="percent">Uploading.. {Math.floor(transferred/totalSize*100)}%</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>}
            {isUploading && <LoadingSpinner />}
        </div>
    )
}

export default MeterialFinderModal